import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getOrderRezeptSheets,
  getOrderRezeptSheetDetail,
  updateShipDates,
  updateOrderRezeptSheet,
} from '@/services/admin/order-rezept-sheets';
import type {
  OrderRezeptSheetListParams,
  OrderRezeptSheetDetailResponse,
  UpdateShipDatePayload,
  UpdateShipDateResponse,
  UpdateOrderRezeptSheetPayload,
  UpdateOrderRezeptSheetResponse,
} from '@/services/admin/order-rezept-sheets';
import useAppToast from '@/hooks/useAppToast';
import {
  getApiErrorMessage,
  isNotFoundError,
  DEFAULT_CONFLICT_ERROR_MESSAGE,
} from '@/utils/apiError';

export type { OrderRezeptSheetListParams } from '@/services/admin/order-rezept-sheets';

export const useAdminOrderRezeptSheetsList = (
  site: string | null,
  params: OrderRezeptSheetListParams
) => {
  const toast = useAppToast();

  const query = useQuery({
    queryKey: ['admin', 'order-rezept-sheets', site ?? '', params],
    queryFn: () => getOrderRezeptSheets(site!, params),
    enabled: !!site,
    staleTime: 0,
  });

  useEffect(() => {
    if (query.isError && query.error) {
      toast.error(getApiErrorMessage(query.error, DEFAULT_CONFLICT_ERROR_MESSAGE));
    }
  }, [query.isError, query.error, toast]);

  return query;
};

export const useUpdateShipDateMutation = (site: string | null) => {
  const toast = useAppToast();
  const queryClient = useQueryClient();

  const { mutateAsync: updateShipDateMutation, isPending: isLoading } = useMutation<
    UpdateShipDateResponse,
    Error,
    UpdateShipDatePayload
  >({
    mutationFn: payload => updateShipDates(site!, payload),
    onSuccess: response => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'order-rezept-sheets'] });
        toast.success(response.message ?? '出荷日を更新しました。');
      } else {
        toast.error(response.message ?? '更新に失敗しました。');
      }
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, '出荷日の更新に失敗しました。'));
    },
  });

  return { updateShipDateMutation, isLoading };
};

/** Fetch a single order rezept sheet detail (for edit page). */
export const useAdminOrderRezeptSheetDetail = (site: string | null, id: number) => {
  const toast = useAppToast();

  const query = useQuery<OrderRezeptSheetDetailResponse>({
    queryKey: ['admin', 'order-rezept-sheets', 'detail', site ?? '', id],
    queryFn: () => getOrderRezeptSheetDetail(site!, id),
    enabled: !!site && id > 0,
    staleTime: 0,
    retry: (failureCount, err) => {
      if (isNotFoundError(err)) return false;
      return failureCount < 3;
    },
  });

  useEffect(() => {
    if (query.isError && query.error && !isNotFoundError(query.error)) {
      toast.error(getApiErrorMessage(query.error, DEFAULT_CONFLICT_ERROR_MESSAGE));
    }
  }, [query.isError, query.error, toast]);

  return query;
};

/** Update an order rezept sheet (details add/update/delete). */
export const useUpdateOrderRezeptSheetMutation = (site: string | null) => {
  const toast = useAppToast();
  const queryClient = useQueryClient();

  const { mutateAsync: updateOrderMutation, isPending: isLoading } = useMutation<
    UpdateOrderRezeptSheetResponse,
    Error,
    { id: number; payload: UpdateOrderRezeptSheetPayload }
  >({
    mutationFn: ({ id, payload }) => updateOrderRezeptSheet(site!, id, payload),
    onSuccess: response => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'order-rezept-sheets'] });
        toast.success(response.message ?? '更新しました。');
      } else {
        toast.error(response.message ?? '更新に失敗しました。');
      }
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, DEFAULT_CONFLICT_ERROR_MESSAGE));
    },
    meta: { suppressNotFoundToast: true },
  });

  return { updateOrderMutation, isLoading };
};
